/**
 * Copyright © 2025 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from 'react';
import { createContext } from 'react';
import { Ability, AbilityBuilder } from '@casl/ability';

export const AbilityContext = createContext();

export const defineAbility = (permissionsHash) => {
  const { can, rules } = new AbilityBuilder(Ability);

  if (!permissionsHash || typeof permissionsHash !== 'object') {
    return new Ability(rules);
  }

  Object.entries(permissionsHash).forEach(([scopeKey, permissions]) => {
    if (!Array.isArray(permissions)) {
      return;
    }

    permissions.forEach((perm) => {
      const [subject, action] = perm.split(':');
      if (!subject || !action) return;

      if (scopeKey === 'default') {
        can(action, subject);
      } else {
        const [, scope] = scopeKey.split(':');
        if (scope) {
          can(action, subject, { name: scope });
        }
      }
    });
  });

  return new Ability(rules);
};

export const AbilityProvider = ({ children }) => {
  const [permissions, setPermissions] = React.useState(null);

  const ability = React.useMemo(() => {
    return defineAbility(permissions);
  }, [permissions]);

  const updateAbility = React.useCallback((newPermissions) => {
    setPermissions(prevPermissions => {
      const prevSerialized = JSON.stringify(prevPermissions);
      const newSerialized = JSON.stringify(newPermissions);
      
      if (prevSerialized === newSerialized) {
        return prevPermissions;
      }
      
      return newPermissions;
    });
  }, []);

  const value = React.useMemo(() => ({ ability, updateAbility }), [ability, updateAbility]);

  return (
    <AbilityContext.Provider value={value}>
      {children}
    </AbilityContext.Provider>
  );
};
